import { format } from 'date-fns';
import Image from 'next/image';
import Link from 'next/link';
import { BlogCategoryIcon } from '@/components/BlogCategoryIcon';
import { AvatarIcon } from '@/components/icons/meta/AvatarIcon';
import { ClockIcon } from '@/components/icons/meta/ClockIcon';
import type { Blog } from '@/types/blog';
import { getBlogCategoryFromStrapiBlogCategory } from '@/utils/blogCategoryMappers';
import { cn } from '@/utils/cn';
import { getBlogPath } from '@/utils/paths';

interface BlogCardProps {
  blog: Blog;
  className?: string;
}

export const BlogCard = ({ blog, className }: BlogCardProps) => {
  const {
    Title,
    Category,
    AuthorName,
    HeaderImage,
    Description,
    Slug,
    CreatedOn,
  } = blog.attributes;

  const thumbnailUrl = HeaderImage.data.attributes.url;
  const smallUrl =
    HeaderImage.data.attributes.formats?.small.url ?? thumbnailUrl;

  const blogCategory = getBlogCategoryFromStrapiBlogCategory(Category);

  return (
    <Link
      className={cn(
        'group flex flex-col gap-4 rounded-lg bg-light-gray p-3 transition-colors duration-200 hover:bg-light-blue-o15 sm:p-4 xl:flex-row',
        className
      )}
      href={getBlogPath({ blogCategory, slug: Slug })}
    >
      <div className="relative aspect-video w-full shrink-0 xl:w-64">
        <Image
          alt={Description}
          className="hidden rounded-lg object-cover md:block"
          fill
          src={thumbnailUrl}
        />
        <Image
          alt={Description}
          className="rounded-lg object-cover md:hidden"
          fill
          src={smallUrl}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-2 sm:gap-3">
        <h3 className="line-clamp-2 font-semibold text-sm group-hover:text-blue-500 sm:text-base">
          {Title}
        </h3>

        <p className="line-clamp-3 grow font-normal text-gray text-xs sm:text-sm">
          {Description}
        </p>

        <div className="flex flex-wrap items-center gap-2 text-blue-gray">
          <div className="flex items-center gap-1">
            <AvatarIcon
              className="h-4 w-4"
              desc="Author avatar icon"
              title="Author"
            />
            <span className="truncate font-medium text-[10px] sm:text-xs">
              {AuthorName}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <ClockIcon
              className="h-4 w-4"
              desc="Publication date icon"
              title="Date"
            />
            <span className="font-medium text-[10px] sm:text-xs">
              {format(new Date(CreatedOn), 'dd.MM.yyyy')}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <BlogCategoryIcon blogCategory={blogCategory} className="h-4 w-4" />
            <span className="truncate font-medium text-[10px] sm:text-xs">
              {Category}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};
